/*
 ** Binary Heap implementation in Javascript
 ** From: http://eloquentjavascript.net/1st_edition/appendix2.html
 **
 ** Copyright (c) 2007 Marijn Haverbeke, last modified on November 28 2013.
 **
 ** Licensed under a Creative Commons attribution-noncommercial license.
 ** All code in this book may also be considered licensed under an MIT license.
 */

/**
 * 二叉堆数据结构实现（最小堆）
 * @template T 堆中存储的元素类型
 */
export class BinaryHeap<T> {
  /** 存储堆元素的数组（从索引 0 开始，内部算法处理基于 1 开始的逻辑） */
  content: T[] = []
  /** 用于计算元素分数的函数（决定堆的排序规则） */
  scoreFunction: (element: T) => number

  /**
   * 初始化二叉堆
   * @param {Function} scoreFunction - 分数计算函数，返回值越小优先级越高
   */
  constructor(scoreFunction: (element: T) => number) {
    this.scoreFunction = scoreFunction
  }

  /**
   * 获取安全的分数值，将 NaN/Infinity 转换为 Number.MAX_VALUE
   * @param {number} score - 原始分数
   * @returns {number} 安全的分数值
   * @private
   */
  private getSafeScore(score: number): number {
    return Number.isFinite(score) ? score : Number.MAX_VALUE
  }

  /**
   * 向堆中添加元素，并执行上浮操作以维护堆性质
   * @param {T} element - 要添加的元素
   */
  push(element: T): void {
    // Add the new element to the end of the array.
    this.content.push(element)
    // Allow it to bubble up.
    this.bubbleUp(this.content.length - 1)
  }

  /**
   * 从堆顶移除并返回元素（最小堆返回分数最小的元素）
   * @returns {T | null} 堆顶元素，堆为空时返回 null
   */
  pop(): T | null {
    // Store the first element so we can return it later.
    const result = this.content[0]
    // Get the element at the end of the array.
    const end = this.content.pop()
    // If there are any elements left, put the end element at the start, and let it sink down.
    if (this.content.length > 0) {
      this.content[0] = end! // 非空断言：pop() 在堆非空时必定有值
      this.sinkDown(0)
    }
    return result
  }

  /**
   * 从堆中移除指定元素，并重新平衡堆
   * @param {T} node - 要移除的元素（通过引用匹配）
   */
  remove(node: T): void {
    const length = this.content.length
    // To remove a value, we must search through the array to find it.
    for (let i = 0; i < length; i++) {
      if (this.content[i] !== node) continue
      // When it is found, the process seen in 'pop' is repeated to fill up the hole.
      const end = this.content.pop()
      // If the element we popped was the one we needed to remove, we're done.
      if (i === length - 1) break
      // Otherwise, replace the removed element with the popped one, and rebalance.
      this.content[i] = end!
      this.bubbleUp(i)
      this.sinkDown(i)
      break
    }
  }

  /**
   * 获取堆中元素数量
   * @returns {number} 元素总数
   */
  size(): number {
    return this.content.length
  }

  /**
   * 迭代所有元素
   * @returns {Iterator<T>} 元素迭代器
   */
  *[Symbol.iterator](): Iterator<T> {
    for (const item of this.content) {
      yield item
    }
  }

  /**
   * 清空堆
   */
  clear(): void {
    this.content = []
  }

  /**
   * 查找并移除满足条件的所有元素
   * @param {Function} predicate - 判断函数，返回 true 的元素会被移除
   * @returns {T[]} 被移除的元素数组
   */
  removeAll(predicate: (item: T) => boolean): T[] {
    const removed: T[] = []

    // 从后向前遍历，避免索引问题
    for (let i = this.content.length - 1; i >= 0; i--) {
      if (predicate(this.content[i])) {
        removed.push(this.content[i])
        this.remove(this.content[i])
      }
    }

    return removed
  }

  /**
   * 私有方法：上浮操作，确保新元素移动到正确位置
   * @param {number} n - 待上浮元素的当前索引（0 开始）
   * @private
   */
  private bubbleUp(n: number): void {
    // Fetch the element that has to be moved.
    const element = this.content[n]
    const score = this.getSafeScore(this.scoreFunction(element))
    // When at 0, an element can not go up any further.
    while (n > 0) {
      // Compute the parent element's index (基于 1 开始的数组转换为 0 开始)
      const parentN = Math.floor((n + 1) / 2) - 1
      const parent = this.content[parentN]
      // If the parent has a lesser score, stop moving up.
      if (score >= this.getSafeScore(this.scoreFunction(parent))) break
      // Swap parent and current element, continue bubbling.
      this.content[parentN] = element
      this.content[n] = parent
      n = parentN
    }
  }

  /**
   * 私有方法：下沉操作，确保堆性质在元素移除后恢复
   * @param {number} n - 待下沉元素的当前索引（0 开始）
   * @private
   */
  sinkDown(n: number): void {
    // Look up the target element and its score.
    const length = this.content.length
    const element = this.content[n]
    const elemScore = this.getSafeScore(this.scoreFunction(element))
    let child1Score = 0

    while (true) {
      // Compute the indices of the child elements.
      const child2N = (n + 1) * 2
      const child1N = child2N - 1
      // This is used to store the new position of the element,
      // if any.
      let swap: number | null = null
      child1Score = 0
      // If the first child exists (is inside the array)...
      if (child1N < length) {
        // Look it up and compute its score.
        const child1 = this.content[child1N]
        child1Score = this.getSafeScore(this.scoreFunction(child1))
        // If the score is less than our element's, we need to swap.
        if (child1Score < elemScore) swap = child1N
      }
      // Do the same checks for the other child.
      if (child2N < length) {
        const child2 = this.content[child2N]
        const child2Score = this.getSafeScore(this.scoreFunction(child2))
        if (child2Score < (swap == null ? elemScore : child1Score)) swap = child2N
      }

      // No need to swap further, we are done.
      if (swap == null) break

      // Otherwise, swap and continue.
      this.content[n] = this.content[swap]
      this.content[swap] = element
      n = swap
    }
  }
}
